// Authenticated live-update stream (GET /api/v1/events). Uses fetch rather than
// EventSource so the client can read HTTP status codes (401 session, 409 resync)
// and send Last-Event-ID explicitly on every reconnect.
import type { EventEnvelope } from '@bcio-voice/contracts';
import { backoffDelay } from './backoff.ts';

export interface SseMessage {
  event: string;
  data: string;
  id: string | null;
}

/** Incremental text/event-stream parser (WHATWG rules; comments and retry ignored). */
export class SseParser {
  private buffer = '';
  private data: string[] = [];
  private event = '';
  private id: string | null = null;

  push(chunk: string): SseMessage[] {
    this.buffer += chunk;
    const out: SseMessage[] = [];
    for (;;) {
      const match = /\r\n|\r|\n/.exec(this.buffer);
      if (!match) break;
      // A trailing lone CR may be the first half of CRLF; wait for more input.
      if (match[0] === '\r' && match.index === this.buffer.length - 1) break;
      const line = this.buffer.slice(0, match.index);
      this.buffer = this.buffer.slice(match.index + match[0].length);
      if (line === '') {
        if (this.data.length > 0) out.push({ event: this.event || 'message', data: this.data.join('\n'), id: this.id });
        this.data = [];
        this.event = '';
        this.id = null;
        continue;
      }
      if (line.startsWith(':')) continue;
      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      let value = colon === -1 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);
      if (field === 'data') this.data.push(value);
      else if (field === 'event') this.event = value;
      else if (field === 'id' && !value.includes('\u0000')) this.id = value;
    }
    return out;
  }
}

export type StreamState =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'resyncing'
  | 'offline'
  | 'unauthenticated'
  | 'stopped';

export interface StreamStatus {
  state: StreamState;
  attempt: number;
  nextRetryAt: number | null;
  lastEventAt: number | null;
  /** True once the server confirmed a replay from our cursor or a fresh snapshot. */
  replayed: boolean;
}

/**
 * Server control events. They carry no `id:` (never a replay position) and no event envelope.
 * - `media.access_changed` {reason}: this user's recording/transcript grants changed; the server
 *   closes the stream. Sensitive data must be cleared and the session re-read before reuse.
 * - `auth.policy_changed` {reason}: the session no longer satisfies the role's policy (for example
 *   MFA). The stream stops; a fresh sign-in is required.
 * - `session.expired`: the session is gone or was rotated. The stream stops.
 */
export type ControlEventType = 'media.access_changed' | 'auth.policy_changed' | 'session.expired';
export interface ControlEvent {
  type: ControlEventType;
  reason: string | null;
}
const CONTROL_EVENTS: ReadonlySet<string> = new Set<ControlEventType>(['media.access_changed', 'auth.policy_changed', 'session.expired']);
const REASON = /^[a-z0-9_.]{1,100}$/;
const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Structural check of a durable event envelope (packages/contracts EventSchema). */
export function isEventEnvelope(value: unknown): value is EventEnvelope {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  const aggregate = v.aggregate as Record<string, unknown> | null | undefined;
  return v.schema_version === 1
    && typeof v.id === 'string' && v.id.length > 0 && v.id.length <= 100
    && typeof v.type === 'string' && v.type.length > 0 && v.type.length <= 100
    && typeof v.tenant_id === 'string' && ID.test(v.tenant_id)
    && typeof aggregate === 'object' && aggregate !== null
    && typeof aggregate.type === 'string' && aggregate.type.length <= 40
    && typeof aggregate.id === 'string' && ID.test(aggregate.id)
    && Number.isInteger(aggregate.version) && (aggregate.version as number) >= 1
    && typeof v.data === 'object' && v.data !== null && !Array.isArray(v.data);
}

function controlReason(data: string): string | null {
  try {
    const parsed = JSON.parse(data) as { reason?: unknown } | null;
    return parsed && typeof parsed.reason === 'string' && REASON.test(parsed.reason) ? parsed.reason : null;
  } catch {
    return null;
  }
}

export interface EventStreamOptions {
  url: string;
  fetch?: typeof fetch;
  /** Durable, validated events only. A throwing consumer never stops the stream. */
  onEvent: (event: EventEnvelope) => void;
  /** The cursor expired; refetch authorized resources (calls, agent row, config). */
  onResync: () => void;
  onSessionExpired: () => void;
  /** Control events (see ControlEvent). Called before the stream reconnects or stops. */
  onControl?: (event: ControlEvent) => void;
  onStatus?: (status: StreamStatus) => void;
  random?: () => number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** Abort and reconnect if no bytes (including heartbeats) arrive for this long. */
  staleMs?: number;
  maxAttemptsBeforeOffline?: number;
}

export class EventStream {
  private cursor: string | null = null;
  private controller: AbortController | null = null;
  private timer: unknown = null;
  private watchdog: unknown = null;
  private attempt = 0;
  private running = false;
  private needsResync = false;
  private online = true;
  /** Consecutive control-event reconnects without a durable event in between (loop guard). */
  private controlReconnects = 0;
  private ignored = 0;
  private status: StreamStatus = { state: 'idle', attempt: 0, nextRetryAt: null, lastEventAt: null, replayed: false };
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  constructor(private readonly options: EventStreamOptions) {
    this.fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.now = options.now ?? Date.now;
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? (handle => clearTimeout(handle as ReturnType<typeof setTimeout>));
  }

  get currentCursor(): string | null {
    return this.cursor;
  }

  get currentStatus(): StreamStatus {
    return this.status;
  }

  /** Malformed or unknown messages dropped without touching the cursor (diagnostics). */
  get ignoredCount(): number {
    return this.ignored;
  }

  /** Starts from a snapshot cursor (for example GET /calls meta.event_cursor), or from "now". */
  start(cursor: string | null = null): void {
    this.cursor = cursor;
    this.running = true;
    this.attempt = 0;
    void this.connect();
  }

  stop(): void {
    this.running = false;
    this.abort();
    this.setStatus({ state: 'stopped', nextRetryAt: null });
  }

  /** "Reconnect now" from the status bar; keeps the cursor so events replay. */
  reconnectNow(): void {
    if (!this.running) return;
    this.abort();
    this.attempt = 0;
    void this.connect();
  }

  setOnline(online: boolean): void {
    this.online = online;
    if (!this.running) return;
    if (!online) {
      this.abort();
      this.setStatus({ state: 'offline', nextRetryAt: null });
    } else {
      this.reconnectNow();
    }
  }

  private abort(): void {
    if (this.timer !== null) this.clearTimer(this.timer);
    if (this.watchdog !== null) this.clearTimer(this.watchdog);
    this.timer = null;
    this.watchdog = null;
    this.controller?.abort();
    this.controller = null;
  }

  private setStatus(patch: Partial<StreamStatus>): void {
    this.status = { ...this.status, ...patch, attempt: this.attempt };
    this.options.onStatus?.(this.status);
  }

  private scheduleReconnect(): void {
    if (!this.running) return;
    if (!this.online) {
      this.setStatus({ state: 'offline', nextRetryAt: null });
      return;
    }
    const delay = backoffDelay(this.attempt, this.options.random);
    this.attempt += 1;
    const max = this.options.maxAttemptsBeforeOffline ?? Number.POSITIVE_INFINITY;
    this.setStatus({ state: this.attempt > max ? 'offline' : 'reconnecting', nextRetryAt: this.now() + delay });
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.connect();
    }, delay);
  }

  private armWatchdog(): void {
    const staleMs = this.options.staleMs ?? 35000;
    if (this.watchdog !== null) this.clearTimer(this.watchdog);
    this.watchdog = this.setTimer(() => {
      this.watchdog = null;
      this.controller?.abort();
    }, staleMs);
  }

  private async connect(): Promise<void> {
    if (!this.running) return;
    const controller = new AbortController();
    this.controller = controller;
    this.setStatus({ state: this.needsResync ? 'resyncing' : this.status.state === 'live' || this.attempt > 0 ? 'reconnecting' : 'connecting', nextRetryAt: null });
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    if (this.cursor) headers['Last-Event-ID'] = this.cursor;
    let response: Response;
    try {
      response = await this.fetchImpl(this.options.url, { headers, credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
    } catch {
      if (this.controller === controller) this.scheduleReconnect();
      return;
    }
    if (this.controller !== controller) return;

    if (response.status === 401 || response.status === 403) {
      this.running = false;
      this.abort();
      this.setStatus({ state: 'unauthenticated', nextRetryAt: null });
      this.options.onSessionExpired();
      return;
    }
    if (response.status === 409) {
      // event_resync_required (expired or foreign cursor): drop the cursor, take a fresh
      // snapshot, then ask the app to refetch so no history is silently skipped.
      void response.body?.cancel().catch(() => undefined);
      this.needsResync = true;
      if (this.cursor === null) {
        this.scheduleReconnect();
        return;
      }
      this.cursor = null;
      void this.connect();
      return;
    }
    if (!response.ok || !response.body) {
      void response.body?.cancel().catch(() => undefined);
      this.scheduleReconnect();
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parser = new SseParser();
    this.armWatchdog();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        this.armWatchdog();
        for (const message of parser.push(decoder.decode(value, { stream: true }))) {
          const next = this.handle(message);
          if (next === 'stop') return;
          if (next === 'reconnect_now') {
            this.reconnectAfterControl(controller);
            return;
          }
        }
      }
    } catch {
      // Aborted by the watchdog, stop() or a network failure; fall through to reconnect.
    }
    if (this.controller === controller) {
      if (this.watchdog !== null) this.clearTimer(this.watchdog);
      this.watchdog = null;
      this.scheduleReconnect();
    }
  }

  /**
   * The server closed the stream after a media-access change. Reconnect from the same cursor:
   * the new connection is authorized with the current grants, so the replay boundary is exact.
   * Repeated control closes without progress back off instead of looping.
   */
  private reconnectAfterControl(controller: AbortController): void {
    if (this.controller !== controller || !this.running) return;
    if (this.watchdog !== null) this.clearTimer(this.watchdog);
    this.watchdog = null;
    controller.abort();
    this.controlReconnects += 1;
    if (this.controlReconnects > 2) {
      this.scheduleReconnect();
      return;
    }
    this.attempt = 0;
    this.setStatus({ state: 'reconnecting', nextRetryAt: null });
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.connect();
    }, 0);
  }

  private handle(message: SseMessage): 'continue' | 'stop' | 'reconnect_now' {
    if (message.event === 'snapshot' && message.id === null) {
      let cursor: unknown = null;
      try {
        cursor = (JSON.parse(message.data) as { cursor?: unknown } | null)?.cursor ?? null;
      } catch {
        // A malformed snapshot keeps the previous cursor.
      }
      if (typeof cursor === 'string' && cursor.length > 0 && cursor.length <= 1000) this.cursor = cursor;
      const resynced = this.needsResync;
      this.needsResync = false;
      this.attempt = 0;
      this.setStatus({ state: 'live', nextRetryAt: null, replayed: true });
      if (resynced) this.safely(() => this.options.onResync());
      return 'continue';
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(message.data);
    } catch {
      parsed = undefined;
    }

    // Control events never carry an id and never change the cursor.
    if (message.id === null && CONTROL_EVENTS.has(message.event) && !isEventEnvelope(parsed)) {
      const control: ControlEvent = { type: message.event as ControlEventType, reason: controlReason(message.data) };
      if (control.type === 'media.access_changed') {
        this.setStatus({ state: 'resyncing', nextRetryAt: null });
        this.safely(() => this.options.onControl?.(control));
        return 'reconnect_now';
      }
      this.running = false;
      this.abort();
      this.setStatus({ state: 'unauthenticated', nextRetryAt: null });
      this.safely(() => this.options.onControl?.(control));
      if (control.type === 'session.expired') this.safely(() => this.options.onSessionExpired());
      return 'stop';
    }

    // Durable events must be well-formed envelopes with a replay position; anything else is
    // dropped without advancing the cursor, so a malformed message cannot poison replay.
    if (!message.id || message.id.length > 1000 || !isEventEnvelope(parsed)) {
      this.ignored += 1;
      return 'continue';
    }
    this.cursor = message.id;
    this.controlReconnects = 0;
    this.setStatus({ lastEventAt: this.now() });
    this.safely(() => this.options.onEvent(parsed));
    return 'continue';
  }

  private safely(fn: () => void): void {
    try {
      fn();
    } catch {
      // A consumer error must not stop the stream or lose the cursor.
    }
  }
}
